import React, { useState } from 'react';
import axios from 'axios';
import { 
  Microscope, 
  Tag, 
  Factory, 
  Package, 
  Sparkles, 
  Palette, 
  Droplets, 
  Heart,
  CheckCircle,
  Mail,
  Phone,
  MapPin,
  ArrowRight,
  Award,
  Menu,
  X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import { useProducts } from '../hooks/useProducts';
import {
  companyInfo,
  services,
  certifications,
  productCategories,
  aboutContent
} from '../mockData';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const iconMap = {
  Microscope,
  Tag,
  Factory,
  Package,
  Sparkles,
  Palette,
  Droplets,
  Heart
};

const LandingPage = () => {
  const { toast } = useToast();
  const { products } = useProducts();
  const [formData, setFormData] = useState({
    companyName: '',
    email: '',
    personName: '',
    phone: '',
    productInterest: '',
    quantity: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value) => {
    setFormData(prev => ({ ...prev, productInterest: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/leads`, {
        company_name: formData.companyName,
        person_name: formData.personName,
        email: formData.email,
        phone: formData.phone,
        product_interest: formData.productInterest,
        quantity: formData.quantity,
        message: formData.message
      });
      
      toast({
        title: "Success!",
        description: response.data.message,
      });
      
      setFormData({
        companyName: '',
        email: '',
        personName: '',
        phone: '',
        productInterest: '',
        quantity: '',
        message: ''
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Something went wrong. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToForm = () => {
    document.getElementById('contact-form').scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-b border-gray-200 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <a href="/" className="flex-shrink-0">
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 leading-tight">
                {companyInfo.name}
              </h1>
              <p className="text-[10px] sm:text-xs text-gray-600">
                A Division of {companyInfo.parent}
              </p>
            </a>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-4">
              <a 
                href="/products"
                className="text-gray-700 hover:text-blue-600 font-medium transition-colors"
              >
                Products
              </a>
              <a 
                href="/contact"
                className="text-gray-700 hover:text-blue-600 font-medium transition-colors"
              >
                Contact Us
              </a>
              <Button onClick={scrollToForm} className="bg-blue-600 hover:bg-blue-700 text-white">
                Request Quote
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 text-gray-700 hover:text-blue-600 transition-colors"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu Dropdown */}
        {isMobileMenuOpen && (
          <div className="md:hidden border-t border-gray-200 bg-white">
            <div className="container mx-auto px-4 py-4 space-y-3">
              <a
                href="/products"
                className="flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-blue-50 hover:text-blue-600 rounded-lg transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <Package className="h-5 w-5" />
                <span className="font-medium">Product Catalogue</span>
              </a>
              <a
                href="/contact"
                className="flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-blue-50 hover:text-blue-600 rounded-lg transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <Mail className="h-5 w-5" />
                <span className="font-medium">Contact Us</span>
              </a>
              <button
                onClick={() => {
                  scrollToForm();
                  setIsMobileMenuOpen(false);
                }}
                className="flex items-center gap-3 w-full px-4 py-3 bg-blue-600 text-white hover:bg-blue-700 rounded-lg transition-colors"
              >
                <ArrowRight className="h-5 w-5" />
                <span className="font-medium">Request Quote</span>
              </button>
            </div>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section className="pt-24 md:pt-32 pb-12 md:pb-20 px-4 bg-gradient-to-br from-slate-50 via-blue-50 to-slate-50">
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-2 gap-8 md:gap-12 items-center">
            <div>
              <div className="inline-block px-3 py-1.5 md:px-4 md:py-2 bg-blue-100 text-blue-700 rounded-full text-xs md:text-sm font-medium mb-4 md:mb-6">
                ISO 9001:2015 & FDA Certified
              </div>
              <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4 md:mb-6 leading-tight">
                {companyInfo.tagline}
              </h2>
              <p className="text-base md:text-xl text-gray-600 mb-6 md:mb-8">
                {companyInfo.description}
              </p>
              <div className="flex flex-col sm:flex-row flex-wrap gap-3 md:gap-4">
                <Button onClick={scrollToForm} size="lg" className="bg-blue-600 hover:bg-blue-700 text-white w-full sm:w-auto">
                  Get Started Today
                </Button>
                <Button 
                  onClick={() => document.getElementById('products').scrollIntoView({ behavior: 'smooth' })}
                  variant="outline" 
                  size="lg"
                  className="border-gray-300 hover:bg-gray-50 w-full sm:w-auto"
                >
                  View Product Catalog
                </Button>
              </div>
            </div>
            <div className="relative order-first lg:order-last">
              <img 
                src="https://images.unsplash.com/photo-1764694187667-f28a05a52c0e" 
                alt="VV CosmoTech cosmetics manufacturing facility in Mumbai India - private label beauty products" 
                className="rounded-2xl shadow-2xl w-full"
              />
              <div className="absolute -bottom-4 md:-bottom-6 -left-4 md:-left-6 bg-white p-4 md:p-6 rounded-xl shadow-xl">
                <p className="text-2xl md:text-3xl font-bold text-blue-600">15+</p>
                <p className="text-sm md:text-base text-gray-600">Product Lines</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-10 md:py-20 px-4 bg-white">
        <div className="container mx-auto">
          <div className="text-center mb-8 md:mb-16">
            <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-2 md:mb-4">Our Services</h2>
            <p className="text-base md:text-xl text-gray-600 max-w-2xl mx-auto px-2">
              Your partner for formulation, manufacturing, and full-scale beauty production.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-8 max-w-5xl mx-auto">
            {services.map((service) => {
              const IconComponent = iconMap[service.icon];
              return (
                <Card key={service.id} className="border-gray-200 hover:shadow-lg transition-shadow">
                  <CardContent className="p-3 md:p-6">
                    <div className="w-10 h-10 md:w-14 md:h-14 bg-blue-100 rounded-lg flex items-center justify-center mb-2 md:mb-4 mx-auto md:mx-0">
                      <IconComponent className="w-5 h-5 md:w-7 md:h-7 text-blue-600" />
                    </div>
                    <h3 className="text-sm md:text-xl font-semibold text-gray-900 mb-1 md:mb-3 text-center md:text-left">{service.title}</h3>
                    <p className="text-xs md:text-base text-gray-600 hidden md:block">{service.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-12 md:py-20 px-4 bg-slate-50">
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-2 gap-8 md:gap-12 items-center mb-12 md:mb-16">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 md:mb-6">About VV CosmoTech</h2>
              <p className="text-base md:text-lg text-gray-700 mb-4 md:mb-6 leading-relaxed">{aboutContent.story}</p>
              <p className="text-base md:text-lg text-gray-700 mb-6 md:mb-8 leading-relaxed font-medium">{aboutContent.mission}</p>
              <div className="space-y-3 md:space-y-4">
                <div className="flex items-center gap-3">
                  <Award className="w-5 h-5 md:w-6 md:h-6 text-blue-600 flex-shrink-0" />
                  <p className="text-sm md:text-base text-gray-900 font-semibold">{aboutContent.trustedBy}</p>
                </div>
                <div className="bg-blue-50 p-3 md:p-4 rounded-lg">
                  <p className="text-xs md:text-sm font-semibold text-blue-900 mb-2">Major regions we have worked with:</p>
                  <div className="flex flex-wrap gap-2">
                    {aboutContent.workedWithRegions.map((region, idx) => (
                      <span key={idx} className="bg-blue-600 text-white px-2.5 py-1 md:px-3 md:py-1 rounded-full text-xs md:text-sm font-medium">
                        {region}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 md:gap-6">
              <img 
                src="https://images.unsplash.com/photo-1760960067586-3999b9aae844" 
                alt="Cosmetic research and development laboratory - beauty formulation services India" 
                className="rounded-xl shadow-lg w-full h-40 md:h-auto object-cover"
              />
              <img 
                src="https://images.unsplash.com/photo-1707944746058-4da338d0f827" 
                alt="Expert cosmetics manufacturing team - third party beauty production" 
                className="rounded-xl shadow-lg mt-6 md:mt-8 w-full h-40 md:h-auto object-cover"
              />
              <img 
                src="https://images.unsplash.com/photo-1717386255773-1e3037c81788" 
                alt="ISO certified cosmetics manufacturing facility Mumbai - private label makeup production" 
                className="rounded-xl shadow-lg -mt-6 md:-mt-8 w-full h-40 md:h-auto object-cover"
              />
              <img 
                src="https://images.unsplash.com/photo-1707944745900-ae9f750f2c69" 
                alt="Quality control testing in beauty manufacturing - FDA approved cosmetics" 
                className="rounded-xl shadow-lg w-full h-40 md:h-auto object-cover"
              />
            </div>
          </div>

          {/* Capabilities Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {aboutContent.capabilities.map((capability, index) => (
              <div key={index} className="bg-white p-4 md:p-6 rounded-xl shadow-sm border border-gray-200">
                <CheckCircle className="w-6 h-6 md:w-8 md:h-8 text-green-600 mb-2 md:mb-3" />
                <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-1 md:mb-2">{capability.title}</h3>
                <p className="text-sm md:text-base text-gray-600">{capability.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-12 md:py-16 px-4 bg-blue-600">
        <div className="container mx-auto">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-3 md:mb-4">Certified Excellence</h2>
            <p className="text-sm md:text-base text-blue-100">Meeting international quality and safety standards</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 max-w-3xl mx-auto">
            {certifications.map((cert, index) => (
              <div key={index} className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-4 md:p-6 text-center">
                <Award className="w-6 h-6 md:w-8 md:h-8 text-white mx-auto mb-2 md:mb-3" />
                <p className="text-white text-sm md:text-base font-medium">{cert}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Product Categories */}
      <section className="py-12 md:py-20 px-4 bg-white">
        <div className="container mx-auto">
          <div className="text-center mb-10 md:mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3 md:mb-4">Product Categories</h2>
            <p className="text-lg md:text-xl text-gray-600 mb-4 md:mb-6">
              Comprehensive range across all beauty segments
            </p>
            <a href="/products">
              <Button size="lg" variant="outline" className="border-blue-600 text-blue-600 hover:bg-blue-50">
                View Complete Catalogue
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </a>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4 md:gap-6 max-w-6xl mx-auto">
            {productCategories.map((category) => {
              const IconComponent = iconMap[category.icon];
              return (
                <Card key={category.id} className="border-gray-200 hover:shadow-lg transition-all hover:-translate-y-1 overflow-hidden">
                  <div className="h-36 sm:h-40 md:h-48 overflow-hidden bg-gray-100 relative">
                    <img 
                      src={category.image} 
                      alt={category.altText || category.name}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                    <div className="absolute bottom-2 md:bottom-4 left-2 md:left-4 right-2 md:right-4">
                      <div className="flex items-center gap-2 mb-1 md:mb-2">
                        <div className="w-8 h-8 md:w-10 md:h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                          <IconComponent className="w-4 h-4 md:w-5 md:h-5 text-white" />
                        </div>
                      </div>
                      <h3 className="text-sm md:text-xl font-semibold text-white leading-tight">{category.name}</h3>
                      <p className="text-white/90 text-xs md:text-sm hidden sm:block">{category.description}</p>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Products Preview - Link to Catalogue */}
      <section id="products" className="py-20 px-4 bg-slate-50">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Product Range</h2>
            <p className="text-xl text-gray-600 mb-8">
              Explore our extensive range of 15+ premium beauty products
            </p>
            <a href="/products">
              <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white">
                View Complete Product Catalogue
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </a>
          </div>
          
          {/* Featured Products Preview - Show first 6 */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {products.slice(0, 6).map((product) => (
              <a 
                key={product.id} 
                href="/products"
                className="group"
              >
                <Card className="border-gray-200 hover:shadow-lg transition-all overflow-hidden">
                  <div className="h-32 overflow-hidden bg-gray-100 relative">
                    <img 
                      src={product.image} 
                      alt={product.altText || `${product.name} - ${product.category} manufacturing India`}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                  </div>
                  <CardContent className="p-3">
                    <h3 className="text-sm font-semibold text-gray-900 truncate">{product.name}</h3>
                    <p className="text-xs text-gray-600">{product.category}</p>
                  </CardContent>
                </Card>
              </a>
            ))}
          </div>
          
          <div className="text-center mt-8">
            <p className="text-gray-600 mb-4">...and 9 more products</p>
            <a href="/products">
              <Button variant="outline" size="lg" className="border-blue-600 text-blue-600 hover:bg-blue-50">
                See All Products
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section id="contact-form" className="py-20 px-4 bg-white">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Get Your Custom Quote</h2>
            <p className="text-xl text-gray-600">
              Fill out the form below and our team will get back to you within 24 hours
            </p>
          </div>
          <Card className="border-gray-200 shadow-xl">
            <CardContent className="p-8">
              <form onSubmit={handleSubmit}>
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <Label htmlFor="companyName" className="text-gray-700 font-medium">Company Name *</Label>
                    <Input
                      id="companyName"
                      name="companyName"
                      value={formData.companyName}
                      onChange={handleInputChange}
                      required
                      className="mt-2"
                      placeholder="Your Company Name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="personName" className="text-gray-700 font-medium">Your Name *</Label>
                    <Input
                      id="personName"
                      name="personName"
                      value={formData.personName}
                      onChange={handleInputChange}
                      required
                      className="mt-2"
                      placeholder="Full Name"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <Label htmlFor="email" className="text-gray-700 font-medium">Email Address *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="mt-2"
                      placeholder="your@email.com"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-gray-700 font-medium">Mobile Number *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="mt-2"
                      placeholder="+91 9876543210"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <Label htmlFor="productInterest" className="text-gray-700 font-medium">Product Interest *</Label>
                    <Select value={formData.productInterest} onValueChange={handleSelectChange} required>
                      <SelectTrigger className="mt-2">
                        <SelectValue placeholder="Select a product category" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="lip-products">Lip Products</SelectItem>
                        <SelectItem value="eye-products">Eye Products</SelectItem>
                        <SelectItem value="skin-care">Skin Care</SelectItem>
                        <SelectItem value="hair-care">Hair Care</SelectItem>
                        <SelectItem value="body-care">Body Care</SelectItem>
                        <SelectItem value="private-labeling">Private Labeling</SelectItem>
                        <SelectItem value="third-party-manufacturing">Third Party Manufacturing</SelectItem>
                        <SelectItem value="packaging">Packaging Solutions</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="quantity" className="text-gray-700 font-medium">Estimated Quantity</Label>
                    <Input
                      id="quantity"
                      name="quantity"
                      value={formData.quantity}
                      onChange={handleInputChange}
                      className="mt-2"
                      placeholder="e.g., 5000 units"
                    />
                  </div>
                </div>

                <div className="mb-6">
                  <Label htmlFor="message" className="text-gray-700 font-medium">Additional Details</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows={5}
                    className="mt-2"
                    placeholder="Tell us more about your requirements..."
                  />
                </div>

                <Button 
                  type="submit" 
                  size="lg"
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Inquiry'}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-10 md:py-12 px-4">
        <div className="container mx-auto">
          {/* Mobile Layout: Two columns side by side */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-6 md:gap-8 mb-8">
            {/* Company Info & Contact - Left Column on Mobile */}
            <div className="col-span-1 md:col-span-1">
              <h3 className="text-lg md:text-2xl font-bold mb-3 md:mb-4">{companyInfo.name}</h3>
              <p className="text-gray-400 text-xs md:text-sm mb-2">A Division of {companyInfo.parent}</p>
              <p className="text-gray-400 text-xs md:text-sm mb-4 hidden md:block">{companyInfo.tagline}</p>
              
              {/* Contact Info - Shows below company name on mobile */}
              <div className="space-y-2 md:hidden mt-4">
                <p className="text-xs font-semibold text-gray-300 mb-2">Contact Us</p>
                <a href={`mailto:${companyInfo.email}`} className="flex items-start gap-2 text-gray-400 hover:text-blue-400 transition-colors">
                  <Mail className="w-3.5 h-3.5 text-blue-400 mt-0.5 flex-shrink-0" />
                  <span className="text-xs break-all">{companyInfo.email}</span>
                </a>
                <a href={`tel:${companyInfo.phone}`} className="flex items-start gap-2 text-gray-400 hover:text-blue-400 transition-colors">
                  <Phone className="w-3.5 h-3.5 text-blue-400 mt-0.5 flex-shrink-0" />
                  <span className="text-xs">{companyInfo.phone}</span>
                </a>
              </div>
              
              {/* Instagram Link */}
              <a 
                href="https://www.instagram.com/vvcosmotech?igsh=aWp0cjZkNWp6dHp6&utm_source=qr"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-pink-400 hover:text-pink-300 transition-colors mt-4"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
                <span className="text-xs md:text-sm">Follow us on Instagram</span>
              </a>
            </div>
            
            {/* Services & Quick Links - Right Column on Mobile */}
            <div className="col-span-1 md:col-span-1 text-right md:text-left md:hidden">
              <h4 className="text-sm font-semibold mb-3">Our Services</h4>
              <ul className="space-y-1.5 text-xs text-gray-300">
                <li>Research & Development</li>
                <li>Private Labeling</li>
                <li>Third Party Manufacturing</li>
                <li>Packaging Solutions</li>
              </ul>
              
              {/* Quick Links on Mobile */}
              <div className="mt-4">
                <p className="text-xs font-semibold text-gray-300 mb-2">Quick Links</p>
                <ul className="space-y-1.5 text-xs text-gray-400">
                  <li><a href="/products" className="hover:text-white transition-colors">Products</a></li>
                  <li><a href="/contact" className="hover:text-white transition-colors">Contact Us</a></li>
                </ul>
              </div>
            </div>
            
            {/* Contact Info - Center (Desktop Only) */}
            <div className="hidden md:block text-center">
              <h4 className="text-lg font-semibold mb-4">Contact Information</h4>
              <div className="space-y-3 text-sm">
                <div className="flex items-start justify-center gap-2">
                  <Mail className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-300">{companyInfo.email}</span>
                </div>
                <div className="flex items-start justify-center gap-2">
                  <Phone className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-300">{companyInfo.phone}</span>
                </div>
                <div className="flex items-start justify-center gap-2">
                  <MapPin className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-300">{companyInfo.address}</span>
                </div>
              </div>
            </div>
            
            {/* Services - Right (Desktop Only) */}
            <div className="hidden md:block text-right">
              <h4 className="text-lg font-semibold mb-4">Our Services</h4>
              <ul className="space-y-2 text-sm text-gray-300">
                <li>Research & Development</li>
                <li>Private Labeling</li>
                <li>Third Party Manufacturing</li>
                <li>Packaging Solutions</li>
              </ul>
            </div>
          </div>
          
          {/* Bottom Bar */}
          <div className="border-t border-gray-800 pt-5 md:pt-6">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-3 md:gap-4 text-xs md:text-sm">
              <p className="text-gray-400 text-center sm:text-left">
                &copy; {new Date().getFullYear()} VV CosmoTech. All rights reserved.
              </p>
              <div className="flex items-center gap-4">
                <a 
                  href="/privacy-policy"
                  className="text-gray-400 hover:text-blue-400 transition-colors"
                >
                  Privacy Policy
                </a>
                <a 
                  href="/admin/login"
                  className="text-gray-500 hover:text-blue-400 transition-colors"
                >
                  Admin Login
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
